// Defensive mappers from thread wire payloads to view-models.
// Bad rows are dropped so one corrupt thread does not break the
// history list.

import type {
  ActiveRunPresentationView,
  ChartArtifactView,
  OrchestrationConsultView,
  PendingRunInteractionView,
  RoutingDecisionView,
  RoutingTaskView,
  ThreadDetailView,
  ThreadMessageRecordView,
  ThreadPageView,
  ThreadSummaryView,
  ThreadToolCallView,
  UiArtifactComponent,
  UiArtifactView,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// Prefer the structured payload when present. Older rows may only have
// `result` or `result_text`.
function toToolResultValue(raw: Record<string, unknown>): unknown {
  if (raw.result_payload !== undefined) return raw.result_payload;
  if (raw.result !== undefined) return raw.result;
  if (typeof raw.result_text === 'string') return raw.result_text;
  return undefined;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function toThreadSummaryView(raw: unknown): ThreadSummaryView | null {
  if (!isRecord(raw)) return null;
  const id = toOptionalString(raw.id);
  const agentId = toOptionalString(raw.agent_id);
  if (!id || !agentId) return null;
  return {
    id,
    agentId,
    title: toNullableString(raw.title),
    isActive: raw.is_active === true,
    createdAt: toNullableString(raw.created_at),
    updatedAt: toNullableString(raw.updated_at),
    pinnedAt: toNullableString(raw.pinned_at),
    activeRun: toActiveRunPresentation(raw.active_run),
    hasMessages: raw.has_messages !== false,
  };
}

export function toThreadSummaryList(raw: unknown): ReadonlyArray<ThreadSummaryView> {
  // /sessions returns `{ sessions: [...] }` (the chat-history list).
  // Accept either the wrapper object or a bare array so a future
  // backend change is a one-line update here, not a UI rewrite.
  let rows: unknown[];
  if (Array.isArray(raw)) {
    rows = raw;
  } else if (isRecord(raw) && Array.isArray(raw.sessions)) {
    rows = raw.sessions;
  } else {
    return [];
  }
  return rows
    .map(toThreadSummaryView)
    .filter((t): t is ThreadSummaryView => t !== null);
}

export function toThreadPageView(
  raw: unknown,
  fallback: { limit: number; offset: number },
): ThreadPageView {
  const record = isRecord(raw) ? raw : {};
  return {
    threads: toThreadSummaryList(raw),
    limit: typeof record.limit === 'number' ? record.limit : fallback.limit,
    offset: typeof record.offset === 'number' ? record.offset : fallback.offset,
    hasMore: record.has_more === true,
    nextCursor: typeof record.next_cursor === 'string' ? record.next_cursor : null,
  };
}

function toToolCall(
  raw: unknown,
): ThreadToolCallView | null {
  if (!isRecord(raw)) return null;
  const tool = toOptionalString(raw.tool);
  if (!tool) return null;
  const args = isRecord(raw.args) ? (raw.args as Record<string, unknown>) : {};
  const result = toToolResultValue(raw);
  const turn_number = typeof raw.turn_number === 'number' ? raw.turn_number : undefined;
  const toolCallId = toOptionalString(raw.tool_call_id) ?? toOptionalString(raw.toolCallId);
  return {
    tool,
    args,
    ...(toolCallId ? { toolCallId } : {}),
    ...(result !== undefined ? { result } : {}),
    ...(turn_number !== undefined ? { turn_number } : {}),
  };
}

function toToolCalls(raw: unknown): ThreadToolCallView[] | null {
  if (!Array.isArray(raw)) return null;
  const result = raw.map(toToolCall).filter((tc): tc is NonNullable<typeof tc> => tc !== null);
  return result.length > 0 ? result : null;
}

function toSkillUse(raw: unknown): NonNullable<ThreadMessageRecordView['skillUses']>[number] | null {
  if (!isRecord(raw)) return null;
  const skillName = toOptionalString(raw.skill_name) ?? toOptionalString(raw.skillName);
  if (!skillName) return null;
  const args = isRecord(raw.args) ? raw.args : undefined;
  const toolCallId = toOptionalString(raw.tool_call_id) ?? toOptionalString(raw.toolCallId);
  return {
    skillName,
    ...(args ? { args } : {}),
    ...(toolCallId ? { toolCallId } : {}),
  };
}

function toSkillUses(raw: unknown): ThreadMessageRecordView['skillUses'] {
  if (!Array.isArray(raw)) return null;
  const result = raw.map(toSkillUse).filter((skill): skill is NonNullable<typeof skill> => skill !== null);
  return result.length > 0 ? result : null;
}

function toOrchestrationConsult(raw: unknown): OrchestrationConsultView | null {
  if (!isRecord(raw)) return null;
  const displayName = toOptionalString(raw.display_name);
  if (!displayName) return null;
  return {
    agentId: toOptionalString(raw.agent_id) ?? '',
    displayName,
    rationale: toOptionalString(raw.rationale) ?? '',
  };
}

function toOrchestrationConsults(raw: unknown): OrchestrationConsultView[] | null {
  if (!Array.isArray(raw)) return null;
  const result = raw.map(toOrchestrationConsult).filter((c): c is OrchestrationConsultView => c !== null);
  return result.length > 0 ? result : null;
}

function toNullableRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? (value as Record<string, unknown>) : null;
}

function toPendingRunOptions(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const options = value.filter((option): option is string => typeof option === 'string' && option.length > 0);
  return options.length > 0 ? options : null;
}

function toPendingRunQuestions(value: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(value)) return null;
  const questions = value.filter(isRecord);
  return questions.length > 0 ? questions : null;
}

function toPendingRunInteraction(value: unknown): PendingRunInteractionView | null {
  if (!isRecord(value)) return null;
  const id = toOptionalString(value.id);
  const type = toOptionalString(value.type);
  const status = toOptionalString(value.status);
  if (!id || !status || (type !== 'user_question' && type !== 'approval')) return null;
  return {
    id,
    type,
    status,
    requestId: toNullableString(value.request_id),
    toolCallId: toNullableString(value.tool_call_id),
    question: toNullableString(value.question),
    approvalPrompt: toNullableString(value.approval_prompt),
    options: toPendingRunOptions(value.options),
    questions: toPendingRunQuestions(value.questions),
  };
}

function toActiveRunPresentation(value: unknown): ActiveRunPresentationView | null {
  if (!isRecord(value)) return null;
  const runId = toOptionalString(value.run_id);
  const agentId = toOptionalString(value.agent_id);
  const status = toOptionalString(value.status);
  if (!runId || !agentId || !status) return null;
  return {
    runId,
    requestId: toNullableString(value.request_id),
    agentId,
    status,
    startedAt: toNullableString(value.started_at),
    createdAt: toNullableString(value.created_at),
    pendingInteraction: toPendingRunInteraction(value.pending_interaction),
  };
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function toRoutingTasks(value: unknown): RoutingTaskView[] | null {
  if (!Array.isArray(value)) return null;
  const tasks = value
    .map((item): RoutingTaskView | null => {
      if (!isRecord(item)) return null;
      const taskId = toOptionalString(item.task_id);
      const agentId = toOptionalString(item.agent_id);
      if (!taskId || !agentId) return null;
      return { taskId, agentId, subQuestion: toNullableString(item.sub_question) };
    })
    .filter((task): task is RoutingTaskView => task !== null);
  return tasks.length > 0 ? tasks : null;
}

function toRoutingDecision(value: unknown): RoutingDecisionView | null {
  if (!isRecord(value)) return null;
  const targetAgents = toStringList(value.target_agents);
  if (targetAgents.length === 0) return null;
  return {
    targetAgents,
    decisionType: toNullableString(value.decision_type),
    confidence: toNullableString(value.confidence),
    decisionReason: toNullableString(value.decision_reason),
    scenario: toNullableString(value.scenario),
    tasks: toRoutingTasks(value.tasks),
  };
}

function toNullableNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function toNullableBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function toNullableInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function isNonNull<T>(value: T | null): value is T {
  return value !== null;
}

function toUploadedFiles(value: unknown): ThreadMessageRecordView['uploadedFiles'] {
  if (!Array.isArray(value)) return null;
  const files = value
    .filter(isRecord)
    .map((file) => {
      const filename = toOptionalString(file.filename) ?? toOptionalString(file.original_filename);
      if (!filename) return null;
      return {
        fileId: toOptionalString(file.file_id),
        filename,
        originalFilename: toOptionalString(file.original_filename),
        contentType: toOptionalString(file.content_type),
        sizeBytes: typeof file.size_bytes === 'number' && Number.isFinite(file.size_bytes) ? file.size_bytes : undefined,
        sharePath: toOptionalString(file.share_path),
        threadUploadsPath: toOptionalString(file.thread_uploads_path),
      };
    })
    .filter(isNonNull);
  return files.length > 0 ? files : null;
}

function toGeneratedArtifacts(value: unknown): ThreadMessageRecordView['artifacts'] {
  if (!Array.isArray(value)) return null;
  const artifacts = value
    .filter(isRecord)
    .map((artifact) => {
      const artifactId = toOptionalString(artifact.artifact_id);
      const filename = toOptionalString(artifact.filename);
      if (!artifactId || !filename) return null;
      return {
        artifactId,
        filename,
        contentType: toOptionalString(artifact.content_type),
        sizeBytes: typeof artifact.size_bytes === 'number' && Number.isFinite(artifact.size_bytes) ? artifact.size_bytes : undefined,
        downloadUrl: toOptionalString(artifact.download_url) ?? toOptionalString(artifact.external_url),
        delegatedAgentId: toOptionalString(artifact.delegated_agent_id),
      };
    })
    .filter(isNonNull);
  return artifacts.length > 0 ? artifacts : null;
}

const CHART_ARTIFACT_TYPES = new Set(['bar', 'line', 'pie', 'area', 'scatter', 'radar']);
const CHART_ARTIFACT_SENTIMENTS = new Set(['risk', 'positive', 'neutral']);
const CHART_ARTIFACT_FORMATS = new Set(['number', 'currency', 'percent', 'compact']);
const CHART_ARTIFACT_DIRECTIONS = new Set(['magnitude', 'signed']);

function toChartArtifactRow(raw: unknown): Record<string, string | number> | null {
  if (!isRecord(raw)) return null;
  const row: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' || typeof value === 'number') row[key] = value;
  }
  // A category label is frequently a bare number (a year, a quarter index) in
  // older persisted rows — coerce rather than reject so one non-string label
  // doesn't silently drop every row in the chart.
  if (typeof row.label === 'number') row.label = String(row.label);
  return typeof row.label === 'string' ? row : null;
}

// Rows persisted before this feature's schema rewrite used the parallel-array
// shape (`labels` + `series[].data`) instead of one row object per category.
// Reload for those older threads would otherwise silently show no chart at
// all — convert to the canonical row-object shape the rest of this function
// expects, mirroring the backend's (now-historical) `_normalize_parallel_array_shape`.
function toLegacyRowShape(raw: Record<string, unknown>): { data: unknown[]; series: unknown[] } | null {
  const labels = raw.labels;
  const legacySeries = raw.series;
  if (!Array.isArray(labels) || !Array.isArray(legacySeries)) return null;

  const rows: Array<Record<string, unknown>> = labels.map((label) => ({ label: String(label) }));
  const seriesMeta: unknown[] = [];
  for (const entry of legacySeries) {
    if (!isRecord(entry) || typeof entry.name !== 'string') continue;
    const values = entry.data;
    if (Array.isArray(values)) {
      values.forEach((value, i) => {
        if (i < rows.length) rows[i][entry.name as string] = value;
      });
    }
    seriesMeta.push({ name: entry.name, ...(entry.sentiment ? { sentiment: entry.sentiment } : {}) });
  }
  return { data: rows, series: seriesMeta };
}

function toChartArtifact(raw: unknown): ChartArtifactView | null {
  if (!isRecord(raw)) return null;
  const artifactId = toOptionalString(raw.artifact_id);
  const type = toOptionalString(raw.type);
  if (!artifactId || !type || !CHART_ARTIFACT_TYPES.has(type)) return null;
  const legacy = !Array.isArray(raw.data) ? toLegacyRowShape(raw) : null;
  const rawData = legacy ? legacy.data : raw.data;
  const rawSeries = legacy ? legacy.series : raw.series;
  if (!Array.isArray(rawData) || !Array.isArray(rawSeries)) return null;
  const data = rawData.map(toChartArtifactRow).filter(isNonNull);
  if (data.length === 0) return null;
  const series = rawSeries
    .filter(isRecord)
    .map((s) => {
      const name = toOptionalString(s.name);
      if (!name) return null;
      const sentiment = toOptionalString(s.sentiment);
      return {
        name,
        ...(sentiment && CHART_ARTIFACT_SENTIMENTS.has(sentiment)
          ? { sentiment: sentiment as ChartArtifactView['series'][number]['sentiment'] }
          : {}),
      };
    })
    .filter(isNonNull);
  const format = toOptionalString(raw.format);
  const direction = toOptionalString(raw.direction);
  return {
    artifactId,
    type: type as ChartArtifactView['type'],
    title: toNullableString(raw.title),
    insight: toNullableString(raw.insight),
    format: format && CHART_ARTIFACT_FORMATS.has(format) ? (format as ChartArtifactView['format']) : 'number',
    direction: direction && CHART_ARTIFACT_DIRECTIONS.has(direction) ? (direction as ChartArtifactView['direction']) : 'signed',
    data,
    series,
  };
}

export function toChartArtifacts(value: unknown): ChartArtifactView[] | null {
  if (!Array.isArray(value)) return null;
  const charts = value.map(toChartArtifact).filter(isNonNull);
  return charts.length > 0 ? charts : null;
}

const UI_ARTIFACT_COMPONENTS = new Set<UiArtifactComponent>(['VegaLiteChart', 'Mermaid', 'Svg']);

// Validated per component (the backend parser only ever emits one shape per
// component — see services/chat/ui_artifacts.py) rather than a closed schema
// like toChartArtifact above: `props` is open-ended by design.
function hasValidProps(component: UiArtifactComponent, props: Record<string, unknown>): boolean {
  if (component === 'VegaLiteChart') return isRecord(props.spec);
  if (component === 'Mermaid') return typeof props.syntax === 'string';
  return typeof props.markup === 'string';
}

function toUiArtifact(raw: unknown): UiArtifactView | null {
  if (!isRecord(raw)) return null;
  const artifactId = toOptionalString(raw.artifact_id);
  const component = toOptionalString(raw.component);
  if (!artifactId || !component || !UI_ARTIFACT_COMPONENTS.has(component as UiArtifactComponent)) return null;
  const props = raw.props;
  if (!isRecord(props) || !hasValidProps(component as UiArtifactComponent, props)) return null;
  return { artifactId, component: component as UiArtifactComponent, props };
}

export function toUiArtifacts(value: unknown): UiArtifactView[] | null {
  if (!Array.isArray(value)) return null;
  const artifacts = value.map(toUiArtifact).filter(isNonNull);
  return artifacts.length > 0 ? artifacts : null;
}

function toQuote(value: unknown): { text: string; messageId?: string } | null {
  if (!isRecord(value)) return null;
  const text = toOptionalString(value.text);
  if (!text) return null;
  const messageId = toOptionalString(value.messageId ?? value.message_id);
  return messageId ? { text, messageId } : { text };
}

function toThreadMessageRecord(raw: unknown, threadId: string): ThreadMessageRecordView | null {
  if (!isRecord(raw)) return null;
  const id = toOptionalString(raw.id);
  if (!id) return null;
  const rawResponse = toNullableRecord(raw.raw_response);
  return {
    id,
    role: toOptionalString(raw.role) ?? 'assistant',
    content: toOptionalString(raw.content) ?? '',
    format: toNullableString(raw.format),
    orchestrationPreamble: toNullableString(raw.orchestration_preamble),
    orchestrationConsults: toOrchestrationConsults(raw.orchestration_consults),
    connectRequired: toStringList(raw.connect_required),
    runId: toNullableString(raw.run_id),
    threadId,
    requestId: toNullableString(raw.request_id) ?? toNullableString(rawResponse?.request_id),
    createdAt: toNullableString(raw.created_at),
    // Debug/enrichment fields — thinking_stream is the string form; thinking is a structured list
    toolCalls: toToolCalls(raw.tool_calls),
    skillUses: toSkillUses(raw.skill_uses),
    thinking: toNullableString(raw.thinking_stream),
    executionTimeMs: toNullableNumber(raw.execution_time_ms),
    turnsUsed: toNullableInt(raw.turns_used),
    rawRequest: toNullableRecord(raw.raw_request),
    rawResponse,
    sourceLabel: toNullableString(raw.source_label),
    routingDecision: toRoutingDecision(raw.routing_decision),
    timedOut: toNullableBoolean(raw.timed_out),
    runSource: toNullableString(raw.run_source),
    uploadedFiles: toUploadedFiles(raw.uploaded_files),
    threadUploadsPath: toNullableString(raw.thread_uploads_path),
    chartArtifacts: toChartArtifacts(raw.chart_artifacts),
    uiArtifacts: toUiArtifacts(raw.ui_artifacts),
    artifacts: toGeneratedArtifacts(raw.artifacts),
    modelName: toNullableString(raw.model_name),
    inputTokens: toNullableNumber(raw.input_tokens),
    outputTokens: toNullableNumber(raw.output_tokens),
    cacheReadInputTokens: toNullableNumber(raw.cache_read_input_tokens),
    cacheCreationInputTokens: toNullableNumber(raw.cache_creation_input_tokens),
    contextWindow: toNullableNumber(raw.context_window),
    quote: toQuote(raw.quote),
  };
}

export function toThreadDetailView(raw: unknown): ThreadDetailView | null {
  if (!isRecord(raw)) return null;
  const id = toOptionalString(raw.id);
  const agentId = toOptionalString(raw.agent_id);
  if (!id || !agentId) return null;
  const messagesRaw = Array.isArray(raw.messages) ? raw.messages : [];
  const messages = messagesRaw
    .map((message) => toThreadMessageRecord(message, id))
    .filter((m): m is ThreadMessageRecordView => m !== null);
  return {
    id,
    agentId,
    title: toNullableString(raw.title),
    pinnedAt: toNullableString(raw.pinned_at),
    activeRun: toActiveRunPresentation(raw.active_run),
    messages,
  };
}
